import {set as setStat} from './stats';
import {getConfigItem, configConstants} from '@servicenow/ui-config';
import createElementClass from './createElementClass';
import {resolveRenderer} from './utils';
import log from './log';
import validateConfig, {validatePresentationalConfig} from './validateConfig';
const {CHECK_FOR_POSSIBLE_PRESENTATIONAL_COMPONENTS, POSSIBLE_PRESENTATIONAL_COMPONENTS} =
	configConstants;
const checkForPossiblePresentationalComponents = getConfigItem(
	CHECK_FOR_POSSIBLE_PRESENTATIONAL_COMPONENTS
);
const possiblePresentationalComponents = getConfigItem(POSSIBLE_PRESENTATIONAL_COMPONENTS);

/**
 * Define a custom element.
 * @function createCustomElement
 * @example
 * import {createCustomElement} from '@servicenow/ui-core';
 *
 * createCustomElement('sn-hello-world', {
 *   properties: {
 *     name: {
 *       default: 'Fred'
 *     }
 *   },
 *   transformState: ({properties}) => properties,
 *   view: ({name}) => <p>Hello {name}!</p>
 * });
 * @param {string} elementName - The tag name of the custom element.
 * @param {Object} config - The custom element configuration.
 * @param {function} [config.setInitialState] - Function that returns the initial state for a component.
 * @param {Object} [config.properties] - The properties used by a custom element.
 * @param {*} [config.properties.&lt;name&gt;.default] - The default value of the property.
 * @param {function} [config.properties.&lt;name&gt;.computed] - A function that computes the property value.
 * @param {boolean} [config.properties.&lt;name&gt;.reflect] - Reflect property change to corresponding attribute. This value is `false` by default, but for a limited, finite subset of keys that overlap with HTML Attribute keys, it is `true` and cannot be set to `false`.
 * @param {boolean} [config.properties.&lt;name&gt;.unstableParse] - Deprecated, use Type Schemas instead. Parse attribute values when corresponding properties are set. This value is `false` by default.
 * @param {boolean} [config.properties.&lt;name&gt;.unstablePreserveEmptyStr] - Deprecated, use Type Schemas instead. Preserve an empty string when attribute, property is present instead of converting the value to a boolean.
 * @param {boolean} [config.properties.&lt;name&gt;.readOnly] - Property cannot be externally set. This value is `false` by default.
 * @param {boolean} [config.properties.&lt;name&gt;.schema] - Schema that will validate the passed in value of the property during development. This value is `null` by default.
 * @param {boolean} [config.properties.&lt;name&gt;.selectable] - A selectable property can be selected or pointed by other component and map to its local property.
 * @param {boolean} [config.properties.&lt;name&gt;.shouldRenderWhenSet] - Property value change should trigger component render. This value is `true` by default.
 * @param {Object} [config.actions] - The actions the custom element dispatches.
 * @param {boolean} [config.actions.&lt;type&gt;.schema] - Schema that will validate if the payload of the action dispatched from this element is correct.
 * @param {Object} [config.actionHandlers] - Handlers to run when the action type is dispatched.
 * @param {function} [config.actionHandlers.&lt;type&gt;] - The action handler as an effect function or config object.
 * @param {function} [config.view] - A function that renders transformed component state and properties to virtual DOM.
 * @param {function} [config.transformState] - A function that executes before a component's state is passed to the view.
 * @param {Array} [config.behaviors] - Behaviors applied to the custom element.
 * @param {Object} [config.shadowRootOptions] - Options applied to the shadowRoot of a custom element.
 * @param {String} [config.shadowRootOptions.mode] - Mode of the shadowRoot.  Allowed values are `open` and `closed`.  When `closed` access is denied to `shadowRoot` from the element.  This value is `open` by default.
 */
export default function createCustomElementFactory(componentRegistry, options = {}) {
	const {presentational = false} = options;

	return function createCustomElement(name, config = {}) {
		if (customElements.get(name))
			return log(`The custom element, ${name}, has already been defined.`);

		if (presentational) {
			validatePresentationalConfig(name, config);
		} else {
			if (
				checkForPossiblePresentationalComponents &&
				validatePresentationalConfig(name, config, {shouldLog: false})
			)
				possiblePresentationalComponents.push(name);

			validateConfig(name, config);
		}

		const renderer = resolveRenderer(config);
		customElements.define(
			name,
			createElementClass({
				presentational,
				...config,
				renderer,
				name,
				componentRegistry
			})
		);

		setStat('defined', name.toLowerCase());
	};
}
